import { backgroundDomain } from '@trello/config';
import debounce from 'debounce';

// photo is of form http://amazon/more/more/photo-xxxxxxx if it's an unsplash photo
// extract the `/photo-xxxxxx` portion
function idFromUrl(url: string) {
  const results = /(\/photo-)[^&.?\n#]*/.exec(url);
  return results ? results[0] : undefined;
}

const APP_ID = '7066';
const BASE_URL = 'https://views.unsplash.com/v?app_id=' + APP_ID;
const TRACK_BY_ID_URL_PREFIX = `${BASE_URL}&filepath=`;
const MAX_URL_LENGTH = 2083;
const MAX_CHUNK_LENGTH = MAX_URL_LENGTH - TRACK_BY_ID_URL_PREFIX.length;
const FILEPATH_SEPARATOR = ',';
const TRACKING_DEBOUNCE_MS = 1000;
const DEFAULT_INTERVAL_MS = 10 * 60 * 1000;

interface QueueEntry {
  filepath: string;
  intervalMs: number;
}

export function getTrackingUrl(ids: string[]) {
  return `${TRACK_BY_ID_URL_PREFIX}${ids
    .map((id) => encodeURIComponent(id))
    .join(FILEPATH_SEPARATOR)}`;
}

export function getTrackingUrls(
  ids: string[],
  maxChunkLength = MAX_CHUNK_LENGTH,
) {
  let currentChunk: string[] = [];
  const chunks: string[][] = [];

  let remainingLength = 0;

  ids.forEach((id) => {
    const encoded = encodeURIComponent(id);

    if (remainingLength < encoded.length + FILEPATH_SEPARATOR.length) {
      currentChunk = [];
      remainingLength = maxChunkLength;
      chunks.push(currentChunk);
    }
    currentChunk.push(id);
    remainingLength -= encoded.length + FILEPATH_SEPARATOR.length;
  });

  return chunks.map(getTrackingUrl);
}

export class Tracker {
  private trackingQueue: QueueEntry[] = [];
  private lastTrackedTimestamp: Map<string, number> = new Map();
  private sendQueuedDebounced: () => void;

  constructor() {
    this.sendQueuedDebounced = debounce(this.sendQueued, TRACKING_DEBOUNCE_MS);
  }

  private sendQueued() {
    const now = Date.now();

    const newIds = this.trackingQueue
      .filter(({ filepath, intervalMs }) => {
        const lastTracked = this.lastTrackedTimestamp.get(filepath);
        return lastTracked === undefined || lastTracked + intervalMs < now;
      })
      .map(({ filepath }) => filepath);

    const uniqueNewIds = Array.from(new Set(newIds));

    getTrackingUrls(uniqueNewIds).forEach((url) => fetch(url));

    newIds.forEach((filepath) => this.lastTrackedTimestamp.set(filepath, now));

    this.trackingQueue = [];
  }

  trackOncePerInterval(
    urlOrUrls: string | string[],
    intervalMs: number = DEFAULT_INTERVAL_MS,
  ): void {
    if (!Array.isArray(urlOrUrls)) {
      return this.trackOncePerInterval([urlOrUrls], intervalMs);
    }

    const urls: string[] = urlOrUrls;

    const filepaths = urls
      .filter((url) => url.startsWith(backgroundDomain))
      .map(idFromUrl)
      .filter((idPhoto): idPhoto is string => idPhoto !== undefined);

    if (filepaths.length > 0) {
      this.trackingQueue = [
        ...this.trackingQueue,
        ...filepaths.map((filepath) => ({ filepath, intervalMs })),
      ];
      this.sendQueuedDebounced();
    }
  }
}

export const UnsplashTracker = new Tracker();
